// Requester-facing invite state for a supplier request.
//
// `request.vendor_invited` is a sticky boolean: it is set to true the moment an
// invite is created (supplier-requests.routes.ts) and is never cleared. On its
// own it therefore answers "was an invite ever sent?" — not "can the vendor
// still act on it?". Rendering it directly is why an approved request kept
// reporting "Vendor Invited" long after its 7-day link had lapsed, with nothing
// anywhere telling the requester the link was dead or needed re-sending.
//
// The list endpoint already returns `invite: { id, expires_at, used }`, so the
// real state is derivable client-side without a new round trip.

export const INVITE_STATE = {
  NOT_INVITED: 'NOT_INVITED',
  INVITED: 'INVITED',
  EXPIRED: 'EXPIRED',
  REGISTERED: 'REGISTERED',
};

/**
 * Resolve what actually happened to a request's vendor invite.
 *
 * Order matters: a used invite stays "registered" even once its expiry passes,
 * because the vendor did complete the step — expiry is only meaningful while
 * the link is still the thing we are waiting on.
 */
export const getInviteState = (request, now = new Date()) => {
  if (!request?.vendor_invited) return INVITE_STATE.NOT_INVITED;

  const invite = request.invite;
  // Legacy rows can carry vendor_invited without a joined invite (the invite
  // was hard-deleted, or predates vendor_invite_id). We cannot prove those are
  // expired, so they stay INVITED rather than being mislabelled as dead.
  if (!invite) return INVITE_STATE.INVITED;

  if (invite.used) return INVITE_STATE.REGISTERED;
  if (invite.expires_at && new Date(invite.expires_at).getTime() <= now.getTime()) {
    return INVITE_STATE.EXPIRED;
  }
  return INVITE_STATE.INVITED;
};

/** Days remaining on a live invite; null when unknown or already lapsed. */
export const inviteDaysLeft = (request, now = new Date()) => {
  const expiresAt = request?.invite?.expires_at;
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - now.getTime();
  if (ms <= 0) return null;
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
};

/** How long ago a lapsed invite expired, for the "expired N days ago" hint. */
export const inviteExpiredAgo = (request, now = new Date()) => {
  const expiresAt = request?.invite?.expires_at;
  if (!expiresAt) return null;
  const ms = now.getTime() - new Date(expiresAt).getTime();
  if (ms <= 0) return null;
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  if (days <= 0) return 'expired today';
  return days === 1 ? 'expired 1 day ago' : `expired ${days} days ago`;
};
